import React from 'react';
import { alpha, Box, Card, Stack, Typography, useTheme } from '@mui/material';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import { formatUsdMoney } from '../../../utils/animatedNumber';
import { formatCompactUsd } from '../dashboardAnalytics.utils';
import ExportButtons from '../../services/sextforce/metrics/account/performanceStats/ExportButtons';

type EarningsTrendCardProps = {
    chartMetricLabel: string;
    chartExportData: Array<Record<string, string | number>>;
    chartFilename: string;
    selectedMetricChartData: Array<{ time: number; value: number }>;
    comparisonMetricChartData: Array<{ time: number; comparisonValue: number }>;
    yAxisDomain: [number, number];
    rangePreset: '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';
    currentLegendDateRange: string | null;
    showComparison: boolean;
    comparisonLegendDateRange: { text: string; vsLabel: string } | null;
    chartPeakLowStat: { maxPt: { time: number; value: number }; minPt: { time: number; value: number } } | null;
    periodChangePercent: number | null;
    peakLabel: string;
    lowestLabel: string;
};

const EarningsTrendCard = React.memo(
    ({
        chartMetricLabel,
        chartExportData,
        chartFilename,
        selectedMetricChartData,
        comparisonMetricChartData,
        yAxisDomain,
        rangePreset,
        currentLegendDateRange,
        showComparison,
        comparisonLegendDateRange,
        chartPeakLowStat,
        periodChangePercent,
        peakLabel,
        lowestLabel,
    }: EarningsTrendCardProps) => {
        const theme = useTheme();

        return (
            <Card sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>
                        {chartMetricLabel} Trend
                    </Typography>
                    <ExportButtons data={chartExportData} componentType="report" filename={chartFilename} />
                </Stack>
                <Box sx={{ width: '100%', height: 260, position: 'relative' }}>
                    <Box
                        component="img"
                        src="/OnlyStruggles.svg"
                        alt=""
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 16,
                            width: { xs: 106, sm: 116 },
                            opacity: 0.2,
                            pointerEvents: 'none',
                            userSelect: 'none',
                            zIndex: 2,
                        }}
                    />
                    {selectedMetricChartData.length === 0 ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography color="text.secondary">No chart data available for selected subscribers.</Typography>
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={selectedMetricChartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="analyticsTrendFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={theme.palette.success.main} stopOpacity={0.5} />
                                        <stop offset="90%" stopColor={theme.palette.success.main} stopOpacity={0.04} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke={theme.palette.divider} strokeOpacity={0.35} vertical={false} />
                                <XAxis
                                    dataKey="time"
                                    type="number"
                                    domain={['dataMin', 'dataMax']}
                                    tickCount={rangePreset === '1y' || rangePreset === 'all' ? 8 : 6}
                                    minTickGap={36}
                                    tickMargin={8}
                                    tickFormatter={(value) =>
                                        rangePreset === '1y' || rangePreset === 'all'
                                            ? moment(value).format('MMM, YYYY')
                                            : moment(value).format('MMM D')
                                    }
                                    tick={{ fontSize: 11 }}
                                    stroke={theme.palette.text.secondary}
                                />
                                <YAxis
                                    width={50}
                                    tickCount={6}
                                    domain={yAxisDomain}
                                    tickFormatter={(value: number) => formatCompactUsd(value)}
                                    tick={{ fontSize: 11 }}
                                    stroke={theme.palette.text.secondary}
                                />
                                <RechartsTooltip
                                    formatter={(value: number, name: string) => [
                                        formatUsdMoney(Number(value ?? 0)),
                                        name === 'comparisonValue' ? 'Comparison' : 'Current',
                                    ]}
                                    labelFormatter={(label) => moment(label).format('ddd, MMM D')}
                                />
                                <Area
                                    type="linear"
                                    data={comparisonMetricChartData}
                                    dataKey="comparisonValue"
                                    stroke={theme.palette.grey[500]}
                                    fillOpacity={0}
                                    strokeDasharray="4 4"
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                                <Area
                                    type="linear"
                                    dataKey="value"
                                    stroke={theme.palette.success.dark}
                                    fill="url(#analyticsTrendFill)"
                                    strokeWidth={2.4}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                    isAnimationActive={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </Box>

                {selectedMetricChartData.length > 0 && currentLegendDateRange && chartPeakLowStat ? (
                    <>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            justifyContent="center"
                            alignItems="center"
                            spacing={3}
                            sx={{ mt: 2, columnGap: 3, rowGap: 1.5 }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 3,
                                        flexShrink: 0,
                                        borderRadius: 1,
                                        bgcolor: 'success.dark',
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                                        Current Period:
                                    </Box>
                                    {currentLegendDateRange}
                                </Typography>
                            </Stack>
                            {showComparison && comparisonLegendDateRange ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 32,
                                            flexShrink: 0,
                                            borderTop: `2px dashed ${theme.palette.grey[500]}`,
                                            mt: '2px',
                                        }}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                                            Comparison Period:
                                        </Box>
                                        {comparisonLegendDateRange.text}
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Stack>

                        <Box
                            sx={{
                                mt: 2,
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'stretch',
                                bgcolor: (themeArg) => alpha(themeArg.palette.primary.main, 0.08),
                                borderRadius: (themeArg) => themeArg.shape.borderRadius,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    py: 2,
                                    px: 2,
                                    textAlign: 'center',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        right: { xs: 14, sm: 0 },
                                        left: { xs: 14, sm: 'auto' },
                                        top: { xs: 'auto', sm: 14 },
                                        bottom: { xs: 0, sm: 14 },
                                        width: { xs: 'auto', sm: '1px' },
                                        height: { xs: '1px', sm: 'auto' },
                                        bgcolor: 'divider',
                                    },
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" display="block">
                                    {comparisonLegendDateRange?.vsLabel ?? 'vs Previous Period'}
                                </Typography>
                                {!showComparison || periodChangePercent === null ? (
                                    <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                                        N/A
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 0.75,
                                            fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: periodChangePercent >= 0 ? 'success.main' : 'error.main',
                                        }}
                                    >
                                        {periodChangePercent >= 0 ? '↑' : '↓'} {Math.abs(periodChangePercent).toFixed(1)}%
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    py: 2,
                                    px: 2,
                                    textAlign: 'center',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        right: { xs: 14, sm: 0 },
                                        left: { xs: 14, sm: 'auto' },
                                        top: { xs: 'auto', sm: 14 },
                                        bottom: { xs: 0, sm: 14 },
                                        width: { xs: 'auto', sm: '1px' },
                                        height: { xs: '1px', sm: 'auto' },
                                        bgcolor: 'divider',
                                    },
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" display="block">
                                    {peakLabel}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                                    {moment.utc(chartPeakLowStat.maxPt.time).format('MMM D, YYYY')}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="primary.main"
                                    sx={{
                                        mt: 0.75,
                                        fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatUsdMoney(chartPeakLowStat.maxPt.value)}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, py: 2, px: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" display="block">
                                    {lowestLabel}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                                    {moment.utc(chartPeakLowStat.minPt.time).format('MMM D, YYYY')}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="primary.main"
                                    sx={{
                                        mt: 0.75,
                                        fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatUsdMoney(chartPeakLowStat.minPt.value)}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                ) : null}
            </Card>
        );
    },
);

EarningsTrendCard.displayName = 'EarningsTrendCard';

export default EarningsTrendCard;
